// =============================================================================
// PEDRA — src/optimizer/guillotine.ts
// Empacotamento GUILHOTINADO de UMA chapa.
//
// Todo corte e PASSANTE: atravessa de ponta a ponta o retangulo que esta sendo
// dividido. Isso e garantia ESTRUTURAL da arvore — cada no só vira dois filhos
// por uma unica linha reta. E exatamente o que uma seccionadora consegue fazer.
//
// A arvore vive numa ARENA (array de nos com indices), nunca em ponteiros e
// nunca com recursao — 2000 pecas nao podem estourar a pilha.
// =============================================================================

import type {
  CutAxis,
  EdgeBanding,
  GrainOrientation,
  Id,
  Micron,
  Micron2,
  Placement,
  Rect,
  Settings,
  StockSheet,
} from '../core/types';
import { allowedOrientations } from '../core/validation';
import { makeRect, residuals } from './geometry';

// -----------------------------------------------------------------------------
// REGRAS
// -----------------------------------------------------------------------------

/**
 * Escolha do retangulo livre que vai receber a peca (menor pontuacao vence):
 *  - `bestArea`      BAF  — menor sobra de area
 *  - `bestShortSide` BSSF — menor sobra no lado curto
 *  - `bestLongSide`  BLSF — menor sobra no lado longo
 *  - `bottomLeft`    BL   — o mais alto e mais a esquerda possivel
 *  - `exactFit`      ECF  — premia o encaixe exato (economiza corte E lamina)
 */
export type FitRule = 'bestArea' | 'bestShortSide' | 'bestLongSide' | 'bottomLeft' | 'exactFit';

/**
 * Direcao do PRIMEIRO dos dois cortes gerados por um posicionamento:
 *  - `shorterAxis` SLAS/SAS — corta pelo menor residuo
 *  - `longerAxis`  LLAS/LAS — corta pelo maior residuo
 *  - `minArea`     MINAS    — produz UM retangulo grande
 *  - `maxArea`     MAXAS    — produz dois retangulos parecidos
 */
export type SplitRule = 'shorterAxis' | 'longerAxis' | 'minArea' | 'maxArea';

// -----------------------------------------------------------------------------
// ARENA DE NOS
// -----------------------------------------------------------------------------

export interface GuillotineNode {
  id: number;
  rect: Rect;
  /** -1 na raiz. */
  parent: number;
  kind: 'free' | 'used' | 'split';
  /** Estagios de guilhotina consumidos ate produzir este no (raiz = 0). */
  stage: number;
  /** Eixo do corte que criou este no (null na raiz). */
  lastAxis: CutAxis | null;
  /** Indice em `PackedSheet.placements` quando `kind === 'used'`; senao -1. */
  placement: number;
  /** Eixo do corte quando `kind === 'split'`. */
  axis: CutAxis | null;
  /** Coordenada onde a lamina COMECA a remover material. */
  cutPos: Micron;
  /** Material destruido por este corte (0 quando o encaixe foi exato). */
  kerf: Micron;
  /** Filho de menor coordenada (esquerda / cima). -1 quando nao existe. */
  childA: number;
  /** Filho de maior coordenada (direita / baixo). -1 quando nao existe. */
  childB: number;
}

/** Uma copia fisica de uma peca, pronta para empacotar. */
export interface PackItem {
  partId: Id;
  instance: number;
  label: string;
  color: string;
  material: string;
  /** Dimensoes DE CORTE na orientacao ORIGINAL (fita de borda ja descontada). */
  width: Micron;
  length: Micron;
  orientation: GrainOrientation;
  edgeBanding: EdgeBanding;
}

export interface PackedSheet {
  /** Arena da arvore; `nodes[0]` e sempre a raiz. */
  nodes: GuillotineNode[];
  placements: Placement[];
  /** Retangulos das folhas ainda livres no fim do empacotamento. */
  freeRects: Rect[];
  usedArea: Micron2;
}

export interface GuillotineOptions {
  fit: FitRule;
  split: SplitRule;
  /** Forca o eixo do PRIMEIRO corte da chapa. `null` = decide pela `split`. */
  firstAxis: CutAxis | null;
}

// -----------------------------------------------------------------------------
// ORIENTACAO E POSICIONAMENTO
// -----------------------------------------------------------------------------

export interface OrientationOption {
  /** Extensao em X. */
  width: Micron;
  /** Extensao em Y. */
  length: Micron;
  rotated: boolean;
}

/**
 * Orientacoes permitidas para esta peca nesta chapa.
 * A regra do veio vem de `allowedOrientations` (core/validation) — nao ha
 * reimplementacao aqui, para que interface e otimizador nunca divirjam.
 * Peca quadrada com rotacao livre gera UMA orientacao so (evita busca duplicada).
 */
export function orientationsFor(
  item: Pick<PackItem, 'width' | 'length' | 'orientation'>,
  sheet: Pick<StockSheet, 'orientation'>,
  considerGrain: boolean,
): OrientationOption[] {
  const allowed = allowedOrientations({ orientation: item.orientation }, sheet, considerGrain);
  const out: OrientationOption[] = [];
  for (const o of allowed) {
    if (o === 'normal') out.push({ width: item.width, length: item.length, rotated: false });
    else out.push({ width: item.length, length: item.width, rotated: true });
  }
  if (out.length > 1 && item.width === item.length) out.length = 1;
  return out;
}

/** Gira a fita de borda junto com a peca: top<-left, right<-top, bottom<-right, left<-bottom. */
export function rotateBanding(b: EdgeBanding): EdgeBanding {
  return { top: b.left, right: b.top, bottom: b.right, left: b.bottom };
}

/** Monta o `Placement` final (dimensoes JA rotacionadas). */
export function makePlacement(
  item: PackItem,
  x: Micron,
  y: Micron,
  width: Micron,
  length: Micron,
  rotated: boolean,
): Placement {
  return {
    partId: item.partId,
    instance: item.instance,
    label: item.label,
    x,
    y,
    length,
    width,
    rotated,
    color: item.color,
    material: item.material,
    edgeBanding: rotated ? rotateBanding(item.edgeBanding) : { ...item.edgeBanding },
  };
}

// -----------------------------------------------------------------------------
// PONTUACAO DE ENCAIXE
// -----------------------------------------------------------------------------

export interface FitScore {
  primary: number;
  secondary: number;
}

/**
 * Pontuacao do encaixe de `w x l` no retangulo livre `r`. Menor = melhor.
 * Pressupoe que a peca JA cabe (`w <= r.width && l <= r.length`).
 */
export function fitScore(r: Rect, w: Micron, l: Micron, rule: FitRule): FitScore {
  const dw = r.width - w;
  const dl = r.length - l;
  const short = dw < dl ? dw : dl;
  const long = dw > dl ? dw : dl;
  switch (rule) {
    case 'bestArea':
      return { primary: r.width * r.length - w * l, secondary: short };
    case 'bestShortSide':
      return { primary: short, secondary: long };
    case 'bestLongSide':
      return { primary: long, secondary: short };
    case 'bottomLeft':
      return { primary: r.y + l, secondary: r.x };
    case 'exactFit':
      return {
        primary: (dw === 0 ? 0 : 1) + (dl === 0 ? 0 : 1),
        secondary: r.width * r.length - w * l,
      };
  }
}

/** Comparacao total e deterministica de duas pontuacoes de encaixe. */
function betterScore(a: FitScore, b: FitScore): boolean {
  if (a.primary !== b.primary) return a.primary < b.primary;
  return a.secondary < b.secondary;
}

// -----------------------------------------------------------------------------
// DECISAO DO EIXO DO PRIMEIRO CORTE
// -----------------------------------------------------------------------------

/**
 * Eixo do primeiro dos dois cortes. `'y'` = corte HORIZONTAL (faixa cheia em
 * largura); `'x'` = corte VERTICAL (coluna cheia em comprimento).
 *
 * Equivalencia MINAS/MAXAS: com corte 'y' os residuos sao (W x ry) e (rx x pl);
 * com 'x' sao (rx x L) e (pw x ry). Como W*ry = pw*ry + rx*ry e
 * rx*L = rx*pl + rx*ry, comparar W*ry com rx*L equivale a comparar
 * pw*ry com rx*pl.
 */
export function decideSplitAxis(
  rule: SplitRule,
  rect: Rect,
  pw: Micron,
  pl: Micron,
  rx: Micron,
  ry: Micron,
): CutAxis {
  let horizontalFirst: boolean;
  switch (rule) {
    case 'shorterAxis':
      horizontalFirst = rx !== ry ? rx < ry : rect.width <= rect.length;
      break;
    case 'longerAxis':
      horizontalFirst = rx !== ry ? rx > ry : rect.width > rect.length;
      break;
    case 'minArea':
      horizontalFirst = pw * ry > rx * pl;
      break;
    case 'maxArea':
      horizontalFirst = pw * ry <= rx * pl;
      break;
  }
  return horizontalFirst ? 'y' : 'x';
}

/** Estagio do corte de eixo `axis` aplicado a `node` (paralelo = mesmo estagio). */
export function cutStageOf(node: GuillotineNode, axis: CutAxis): number {
  return node.lastAxis === null || axis !== node.lastAxis ? node.stage + 1 : node.stage;
}

// -----------------------------------------------------------------------------
// MANIPULACAO DA ARENA
// -----------------------------------------------------------------------------

function newNode(
  nodes: GuillotineNode[],
  rect: Rect,
  parent: number,
  stage: number,
  lastAxis: CutAxis | null,
): number {
  const id = nodes.length;
  nodes.push({
    id,
    rect,
    parent,
    kind: 'free',
    stage,
    lastAxis,
    placement: -1,
    axis: null,
    cutPos: 0,
    kerf: 0,
    childA: -1,
    childB: -1,
  });
  return id;
}

/**
 * Converte `nodeId` num no de corte e cria os filhos.
 * `aSize` e a extensao do filho A no eixo do corte; `bSize` a do filho B.
 * O filho B so nasce quando `bSize > 0` — quando a lamina consome a tira
 * inteira, o corte existe mas nao sobra retangulo nenhum.
 * Devolve o id do filho A (que sempre existe, pois `aSize > 0`).
 */
function emitSplit(
  nodes: GuillotineNode[],
  nodeId: number,
  axis: CutAxis,
  cutPos: Micron,
  kerf: Micron,
  aSize: Micron,
  bSize: Micron,
  newFree: number[],
): number {
  const node = nodes[nodeId] as GuillotineNode;
  const r = node.rect;
  const stage = cutStageOf(node, axis);

  const aRect =
    axis === 'x' ? makeRect(r.x, r.y, aSize, r.length) : makeRect(r.x, r.y, r.width, aSize);
  const a = newNode(nodes, aRect, nodeId, stage, axis);

  let b = -1;
  if (bSize > 0) {
    const bRect =
      axis === 'x'
        ? makeRect(cutPos + kerf, r.y, bSize, r.length)
        : makeRect(r.x, cutPos + kerf, r.width, bSize);
    b = newNode(nodes, bRect, nodeId, stage, axis);
    newFree.push(b);
  }

  node.kind = 'split';
  node.axis = axis;
  node.cutPos = cutPos;
  node.kerf = kerf;
  node.childA = a;
  node.childB = b;
  return a;
}

/**
 * Aplica os (ate dois) cortes que isolam uma peca de `pw x pl` ancorada no
 * canto superior-esquerdo de `nodes[nodeId]`.
 * Devolve o no que contem EXATAMENTE a peca e os novos nos livres criados.
 */
function applySplit(
  nodes: GuillotineNode[],
  nodeId: number,
  pw: Micron,
  pl: Micron,
  kerf: Micron,
  axisFirst: CutAxis,
): { usedId: number; newFree: number[] } {
  const rect = (nodes[nodeId] as GuillotineNode).rect;
  const res = residuals(rect, pw, pl, kerf);
  const newFree: number[] = [];
  let cur = nodeId;

  if (axisFirst === 'y') {
    if (res.ryRaw > 0) {
      cur = emitSplit(nodes, cur, 'y', rect.y + pl, res.ky, pl, res.ry, newFree);
    }
    if (res.rxRaw > 0) {
      const c = (nodes[cur] as GuillotineNode).rect;
      cur = emitSplit(nodes, cur, 'x', c.x + pw, res.kx, pw, res.rx, newFree);
    }
  } else {
    if (res.rxRaw > 0) {
      cur = emitSplit(nodes, cur, 'x', rect.x + pw, res.kx, pw, res.rx, newFree);
    }
    if (res.ryRaw > 0) {
      const c = (nodes[cur] as GuillotineNode).rect;
      cur = emitSplit(nodes, cur, 'y', c.y + pl, res.ky, pl, res.ry, newFree);
    }
  }

  return { usedId: cur, newFree };
}

// -----------------------------------------------------------------------------
// EMPACOTAMENTO DE UMA CHAPA
// -----------------------------------------------------------------------------

interface Candidate {
  freeIdx: number;
  nodeId: number;
  width: Micron;
  length: Micron;
  rotated: boolean;
  score: FitScore;
}

/**
 * Empacota `items` (na ORDEM recebida) numa unica chapa.
 * Cada peca vai para o melhor retangulo livre segundo `opts.fit`; o que nao
 * couber volta em `leftover` — como os retangulos livres so encolhem, uma peca
 * que nao cabe agora nunca mais caberia nesta chapa.
 */
export function packSheetGuillotine(
  sheet: StockSheet,
  items: readonly PackItem[],
  settings: Settings,
  opts: GuillotineOptions,
): { packed: PackedSheet; leftover: PackItem[] } {
  const kerf = Number.isFinite(settings.kerf) && settings.kerf > 0 ? settings.kerf : 0;
  const nodes: GuillotineNode[] = [];
  const placements: Placement[] = [];
  const leftover: PackItem[] = [];
  let usedArea = 0;

  const width = sheet.width > 0 ? sheet.width : 0;
  const length = sheet.length > 0 ? sheet.length : 0;
  newNode(nodes, makeRect(0, 0, width, length), -1, 0, null);

  if (width <= 0 || length <= 0) {
    return {
      packed: { nodes, placements, freeRects: collectFreeRects(nodes), usedArea: 0 },
      leftover: items.slice(),
    };
  }

  const freeIds: number[] = [0];

  // Menor lado ainda necessario a partir de cada posicao: poda retangulos
  // livres que nao servem mais para NENHUMA peca restante.
  const n = items.length;
  const suffixMin = new Array<number>(n + 1);
  suffixMin[n] = Number.POSITIVE_INFINITY;
  for (let i = n - 1; i >= 0; i--) {
    const it = items[i] as PackItem;
    const side = it.width < it.length ? it.width : it.length;
    const nextMin = suffixMin[i + 1] as number;
    suffixMin[i] = side < nextMin ? side : nextMin;
  }

  for (let i = 0; i < n; i++) {
    const item = items[i] as PackItem;

    // --- poda -----------------------------------------------------------------
    const minDim = suffixMin[i] as number;
    if (freeIds.length > 1) {
      let w = 0;
      for (let k = 0; k < freeIds.length; k++) {
        const r = (nodes[freeIds[k] as number] as GuillotineNode).rect;
        if (r.width >= minDim && r.length >= minDim) freeIds[w++] = freeIds[k] as number;
      }
      freeIds.length = w;
    }

    if (item.width <= 0 || item.length <= 0) {
      leftover.push(item);
      continue;
    }

    // --- melhor encaixe -------------------------------------------------------
    const orients = orientationsFor(item, sheet, settings.considerGrain);
    let best: Candidate | null = null;
    for (let k = 0; k < freeIds.length; k++) {
      const nodeId = freeIds[k] as number;
      const r = (nodes[nodeId] as GuillotineNode).rect;
      for (let o = 0; o < orients.length; o++) {
        const or = orients[o] as OrientationOption;
        if (or.width > r.width || or.length > r.length) continue;
        const score = fitScore(r, or.width, or.length, opts.fit);
        if (best === null || betterScore(score, best.score)) {
          best = {
            freeIdx: k,
            nodeId,
            width: or.width,
            length: or.length,
            rotated: or.rotated,
            score,
          };
        }
      }
    }

    if (best === null) {
      leftover.push(item);
      continue;
    }

    // --- posiciona ------------------------------------------------------------
    const node = nodes[best.nodeId] as GuillotineNode;
    const rect = node.rect;
    const res = residuals(rect, best.width, best.length, kerf);
    const axisFirst =
      best.nodeId === 0 && opts.firstAxis !== null
        ? opts.firstAxis
        : decideSplitAxis(opts.split, rect, best.width, best.length, res.rx, res.ry);

    const applied = applySplit(nodes, best.nodeId, best.width, best.length, kerf, axisFirst);
    const usedNode = nodes[applied.usedId] as GuillotineNode;
    const placementIndex = placements.length;
    placements.push(
      makePlacement(
        item,
        usedNode.rect.x,
        usedNode.rect.y,
        best.width,
        best.length,
        best.rotated,
      ),
    );
    usedNode.kind = 'used';
    usedNode.placement = placementIndex;
    usedArea += best.width * best.length;

    freeIds.splice(best.freeIdx, 1);
    for (let k = 0; k < applied.newFree.length; k++) freeIds.push(applied.newFree[k] as number);
  }

  return {
    packed: { nodes, placements, freeRects: collectFreeRects(nodes), usedArea },
    leftover,
  };
}

/** Retangulos de todas as folhas ainda livres (com area positiva). */
export function collectFreeRects(nodes: readonly GuillotineNode[]): Rect[] {
  const out: Rect[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const nd = nodes[i] as GuillotineNode;
    if (nd.kind !== 'free') continue;
    if (nd.rect.width <= 0 || nd.rect.length <= 0) continue;
    out.push({ x: nd.rect.x, y: nd.rect.y, width: nd.rect.width, length: nd.rect.length });
  }
  return out;
}
